import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { fmtMXN } from '@/lib/format';
import { usePeriodSummary } from '@/hooks/usePeriodSummary';
import PeriodPicker from '@/components/PeriodPicker';
import { api, apiErr } from '@/lib/api';
import { toast } from 'sonner';

const Row = ({ label, value, big, tid }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
    <span className="text-sm">{label}</span>
    <span data-testid={tid} className={`font-display font-bold num ${big ? 'text-lg' : 'text-sm'}`}>{value}</span>
  </div>
);

export default function Preform() {
  const { data, loading, refresh } = usePeriodSummary();
  const [sat, setSat] = useState({ date: '', linea_captura: '', acuse: '' });

  if (loading || !data) return <div className="pt-8 text-center text-muted-foreground">Cargando…</div>;
  const { period, summary, activities } = data;

  const saveSAT = async () => {
    if (!sat.date) { toast.error('Fecha requerida'); return; }
    try {
      await api.patch(`/periods/${period.id}`, { status: 'presentado_sat', sat_submission: sat });
      toast.success('Presentación registrada');
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5">
      <PeriodPicker/>
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight">Preformulario</h2>
        <p className="text-xs text-muted-foreground mt-0.5">Para capturar manualmente en el portal del SAT</p>
      </div>

      <Card className="p-4 border-border rounded-xl bg-primary/5">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Ingresos totales</p>
        <p className="font-display text-3xl font-extrabold num mt-1" data-testid="pf-ingresos">{fmtMXN(summary.ingresos_totales)}</p>
        <div className="mt-3 text-xs text-muted-foreground space-y-1">
          {activities.map(a => (
            <div key={a.id} className="flex justify-between"><span>{a.name}</span><span className="num">{fmtMXN(summary.ingresos_por_actividad?.[a.id] || 0)}</span></div>
          ))}
        </div>
      </Card>

      <Card className="p-4 border-border rounded-xl">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">ISR RESICO</p>
        <Row tid="pf-isr-base" label="Base gravable" value={fmtMXN(summary.ingresos_totales)}/>
        <Row tid="pf-isr-tasa" label="Tasa aplicable" value={`${(summary.isr_tasa*100).toFixed(2)} %`}/>
        <Row tid="pf-isr" label="ISR estimado" value={fmtMXN(summary.isr_estimado)} big/>
      </Card>

      <Card className="p-4 border-border rounded-xl">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">IVA</p>
        <Row tid="pf-iva-trasladado" label="IVA trasladado" value={fmtMXN(summary.iva_trasladado)}/>
        <Row tid="pf-iva-acreditable" label="IVA acreditable" value={fmtMXN(summary.iva_acreditable)}/>
        <Row tid="pf-iva-pagar" label="IVA a pagar" value={fmtMXN(summary.iva_a_pagar)} big/>
        <Row tid="pf-iva-favor" label="IVA a favor" value={fmtMXN(summary.iva_a_favor)}/>
      </Card>

      <Card className="p-4 border-border rounded-xl">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Registrar presentación manual</p>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Fecha de presentación</Label>
            <Input type="date" data-testid="sat-date" className="h-12 mt-1" value={sat.date} onChange={(e) => setSat(s => ({ ...s, date: e.target.value }))}/>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Línea de captura</Label>
            <Input data-testid="sat-linea" className="h-12 mt-1" placeholder="Ejemplo 20250315…" value={sat.linea_captura} onChange={(e) => setSat(s => ({ ...s, linea_captura: e.target.value }))}/>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Folio de acuse</Label>
            <Input data-testid="sat-acuse" className="h-12 mt-1" placeholder="Folio del acuse" value={sat.acuse} onChange={(e) => setSat(s => ({ ...s, acuse: e.target.value }))}/>
          </div>
          <Button data-testid="sat-save" onClick={saveSAT} className="w-full h-12 rounded-full">Registrar presentación</Button>
        </div>
      </Card>

      <p className="text-[11px] text-muted-foreground leading-relaxed">
        Estimación pendiente de revisión. La app no presenta declaraciones ni se conecta al portal del SAT. No se guardan contraseñas del SAT, e.firma ni archivos .key.
      </p>
    </div>
  );
}
